/**
 * Background Sync plumbing (offline reliability §1).
 *
 * The outbox used to drain only while a tab was open. Now every queued row
 * asks the browser for a one-shot `sync` under OUTBOX_SYNC_TAG; when the
 * device is back on a network the service worker wakes (src/sw.ts), and
 * either hands the drain to an open tab (DRAIN_MESSAGE) or replays the rows
 * itself. Chromium/Android only — everywhere else these are honest no-ops and
 * the app keeps draining while open.
 */

export const OUTBOX_SYNC_TAG = 'demoops-outbox';

/** Posted by the service worker to every open window on a `sync` event so the
 *  app's SyncProvider drains (single implementation, no double replay). */
export const DRAIN_MESSAGE_TYPE = 'demoops:drain';

export interface DrainMessage { type: typeof DRAIN_MESSAGE_TYPE }

export function isDrainMessage(data: unknown): data is DrainMessage {
  return !!data && typeof data === 'object' && (data as { type?: unknown }).type === DRAIN_MESSAGE_TYPE;
}

type SyncCapableRegistration = ServiceWorkerRegistration & {
  sync?: { register(tag: string): Promise<void> };
};

/** Synchronous, for the UI: can THIS browser run the outbox without a tab? */
export function isBackgroundSyncSupported(): boolean {
  return typeof window !== 'undefined'
    && typeof navigator !== 'undefined'
    && 'serviceWorker' in navigator
    && 'SyncManager' in window;
}

/**
 * Register the outbox sync. Idempotent (re-registering the same tag is a
 * no-op for the browser), safe to call on every enqueue and after every
 * failed attempt. Resolves `true` only when the browser accepted it.
 */
export async function requestOutboxSync(): Promise<boolean> {
  if (!isBackgroundSyncSupported()) return false;
  try {
    const registration = (await navigator.serviceWorker.ready) as SyncCapableRegistration;
    if (!('sync' in registration) || !registration.sync) return false;
    await registration.sync.register(OUTBOX_SYNC_TAG);
    return true;
  } catch {
    // Permission denied, insecure context, or a browser that lies about
    // SyncManager — nothing changes; the app drains while open.
    return false;
  }
}

/** Does the registration this page runs under actually expose `sync`? */
export async function detectBackgroundSync(): Promise<boolean> {
  if (!isBackgroundSyncSupported()) return false;
  try {
    const registration = (await navigator.serviceWorker.ready) as SyncCapableRegistration;
    return 'sync' in registration && !!registration.sync;
  } catch {
    return false;
  }
}

/** Run `onDrain` whenever the service worker posts a drain message. Returns
 *  the unsubscribe; a no-op outside a service-worker-capable window. */
export function subscribeToDrainMessages(onDrain: () => void): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator) || !navigator.serviceWorker) return () => {};
  const listener = (event: MessageEvent) => {
    if (isDrainMessage(event.data)) onDrain();
  };
  navigator.serviceWorker.addEventListener('message', listener);
  return () => navigator.serviceWorker.removeEventListener('message', listener);
}
